import type { RecordAuthor } from '@/shared/types/clients';

export type ID = string;

export enum InvoiceStatus {
  PENDING_BILLING = 'PENDING_BILLING', // À émettre (auto depuis CRA)
  DRAFT = 'DRAFT',
  INVOICED = 'INVOICED', // Facturée
  SENT = 'SENT', // Envoyée
  PAID = 'PAID', // Payée
}
/**
 * Valeur stockée en base (Prisma) : chaîne littérale.
 * - `BILLING_FORM` : facture créée via l’écran de facturation (numéro souvent auto, ce n’est **pas** « saisie manuelle du n° »).
 * - `AUTO_FROM_TIMESHEET` : brouillon généré depuis une feuille de temps.
 * Le libellé Prisma `MANUAL` est historique (confus) ; en TS on lit `BILLING_FORM` (= même valeur).
 */
export enum InvoiceSource {
  AUTO_FROM_TIMESHEET = 'AUTO_FROM_TIMESHEET',
  BILLING_FORM = 'MANUAL',
  IMPORTED = 'IMPORTED',
}

export enum InvoiceKind {
  INVOICE = 'INVOICE',
  CREDIT_NOTE = 'CREDIT_NOTE',
}

export type InvoiceLine = {
  id: ID;
  label: string;
  quantity: number; // nb jours / unités
  /**
   * L'unité de la quantité — « jour », « heure », « mois ».
   *
   * **`null` a un sens** depuis suivora-api#1324 : une ligne d'escompte n'a pas d'unité, parce
   * qu'elle n'a pas de calcul. Sa quantité vaut 1 et son prix unitaire répète son montant ; les
   * afficher les ferait lire comme un tarif (#1770).
   */
  unit?: string | null;
  unitPrice: number; // HT
  vatRate: number; // ex: 0.2
  totalExVat: number;
};

export type DiscountType = 'percentage' | 'amount';

/** Escompte pour paiement anticipé — appliqué sur le HT (réduit aussi la TVA proportionnellement). */
export type InvoiceDiscount = {
  type: DiscountType;
  /** Valeur saisie : % (ex: 2) ou montant HT (ex: 50) */
  value: number;
  /** Montant HT de la remise, calculé côté domaine */
  amount: number;
};

export type Invoice = {
  id: ID;
  companyName: string;
  /** Nom du consultant rattaché (dénormalisé par le BE). */
  consultantName?: string | null;
  /** Id du consultant rattaché — saisi à la création / édition ou issu d'un CRA. */
  consultantId?: string | null;
  companyEmail?: string;
  /** Aligné sur l’API (colonne) — le mock peut encore servir l’adresse via meta. */
  companyAddress?: string;
  companySiret?: string;
  companyVat?: string;
  number?: string; // assigné à l’envoi
  kind?: InvoiceKind;
  /**
   * Sur un **avoir** : la facture qu'il corrige (suivora-api#1346).
   *
   * Le rattachement vivait dans `meta.originalInvoiceId` — un identifiant enfoui dans du JSON, que
   * le serveur ne joignait ni n'indexait. Il est désormais servi en colonne, dans les deux sens.
   */
  creditNoteOfId?: string | null;
  /**
   * Sur une **facture** : l'avoir qui l'annule, `null` tant qu'elle ne l'est pas.
   *
   * **Ne se déduit pas.** Rapprocher par ressemblance — même client, même période, montants opposés
   * — se trompe sur une mission reconduite, où deux factures identiques se suivent.
   */
  creditedByInvoiceId?: string | null;
  /** La date de l'avoir qui annule cette facture, servie avec lui. */
  creditedAt?: string | null;
  status: InvoiceStatus;
  source: InvoiceSource;
  currency: 'EUR';
  period?: string; // ex: "2025-10"
  issuedAt?: string;
  /**
   * Identifiant Pennylane, posé par l'émission — et par elle seule. Il sépare une facture
   * réellement émise d'un brouillon approuvé, deux situations que le statut `INVOICED` confondait
   * (#1463).
   */
  pennylaneInvoiceId?: string | null;
  /** La mission facturée — pour lire l'accès au portail où la déposer (#2505). */
  missionId?: string | null;
  /** Le départ du courrier — ou, sur une facture portail, **la date du dépôt** (#2505). */
  sentAt?: string | null;
  /** Par où elle parvient au client, figé à l'émission. `null` = pas encore émise (#2505). */
  deliveryChannel?: 'EMAIL' | 'PORTAL' | null;
  /** Qui a consigné le dépôt sur le portail. `null` sur une facture partie par e-mail (#2505). */
  portalDepositedBy?: RecordAuthor | null;
  /**
   * Dernière **tentative** d'envoi au client — à ne pas confondre avec `sentAt`, qui dit qu'un
   * courrier est **parti** (#1470, suivora-api#1091).
   */
  lastSendAttemptAt?: string | null;
  /**
   * Ce qui a empêché le dernier envoi, ou `null` s'il a réussi — le serveur efface la raison au
   * succès. Message **libre** : il se rend tel quel, jamais reclassé.
   */
  lastSendError?: string | null;
  /**
   * Relances de paiement envoyées pour cette facture (#1554, suivora-api#1137).
   *
   * La trace vit sur la facture et non sur le client — c'est elle qu'on relance, et une facture
   * réglée doit sortir du décompte sans effacer l'historique des autres. L'écran agrège par
   * client ; l'inverse serait irréversible.
   */
  remindersSentCount?: number;
  /** Dernière relance, ou `null` si le client n'a jamais été relancé sur cette facture. */
  lastReminderAt?: string | null;
  dueDate?: string;
  /**
   * Le jour du règlement, quand la facture est réglée (suivora-api `InvoiceResponseDto.paidAt`).
   *
   * Il était servi et **non déclaré ici** : le champ arrivait dans la réponse, le compilateur
   * affirmait qu'il n'existait pas, et la question qui suit « Payée » — *quand ?* — restait sans
   * réponse (#1916).
   *
   * ⚠️ **Il n'est fiable que pour les factures réglées dans l'application.** Le connecteur ne rend
   * pas `paid_at`, et la synchronisation retombe alors sur l'heure de sa passe : les factures
   * reprises portent donc la date de leur première synchronisation, pas celle de leur virement.
   */
  paidAt?: string | null;
  /**
   * Délai de règlement prêt à afficher, résolu par le BE (mission → client →
   * défaut tenant) : « 30 jours », « 45 jours fin de mois », « À réception »… ou
   * `null`. Le front l'affiche tel quel (PR BE #318) — pas de reconstruction.
   */
  paymentDelayLabel?: string | null;
  /** Délai résolu en jours (brut, pour calculs). */
  paymentDelayDays?: number | null;
  /** Nuance « fin de mois ». */
  paymentTermEndOfMonth?: boolean | null;
  purchaseOrder?: string;
  subtotal: number; // HT avant escompte
  discount?: InvoiceDiscount;
  vatAmount: number;
  /**
   * Le **régime de TVA** appliqué à cette facture (#2151, suivora-api#1612), figé à sa création
   * comme l'escompte : le pays d'un client peut changer, une facture partie ne se réécrit pas.
   *
   * `FRENCH_VAT` — TVA française (France, ou société de l'UE **sans** numéro de TVA).
   * `EU_REVERSE_CHARGE` — autoliquidation par un assujetti d'un autre État membre, 0 %.
   * `OUTSIDE_SCOPE` — prestation hors champ, destinataire hors UE, 0 %.
   *
   * ⚠️ **Deux régimes portent un taux nul.** Sans ce champ, un 0 % se lit comme une erreur de
   * saisie, et rien ne dit lequel s'appliquait.
   *
   * ⚠️ **`null` n'est pas « TVA française »** : les factures antérieures à la livraison n'en
   * portent pas. « On ne sait pas » et « 20 % » sont deux choses différentes, et un repli
   * affirmerait un régime que personne n'a appliqué.
   */
  vatRegime?: 'FRENCH_VAT' | 'EU_REVERSE_CHARGE' | 'OUTSIDE_SCOPE' | null;
  /**
   * La mention légale portée par le document, figée avec le régime.
   *
   * ⚠️ **À afficher telle quelle, jamais à recomposer ni à traduire.** Une facture à 0 % sans sa
   * mention n'est pas allégée : elle est **irrégulière**, et c'est le contrôle fiscal du client qui
   * la refuse. Une mention d'ordre fiscal recopiée dans trois clients diverge au premier changement
   * de texte — le serveur fait foi.
   *
   * `null` en régime français : il n'y a rien à justifier.
   */
  vatLegalMention?: string | null;
  total: number; // TTC après escompte
  lines: InvoiceLine[];
  meta?: Record<string, any>; // { timesheetId, consultantId, ... }
  /** Drapeau calculé BE : facture émise, non réglée, échéance dépassée. */
  overdue?: boolean;
  /** URL du PDF (S3) - disponible pour factures envoyées/payées */
  pdfUrl?: string;
  createdAt?: string; // ISO date — pour filtrer les brouillons par année
  updatedAt?: string; // ISO date
  /**
   * Qui a émis la facture, et **comment** (suivora-api#974).
   *
   * Même forme que sur la fiche client et la fiche contact : la nature porte le sens, le nom
   * l'enrichit quand il existe. `{ kind: 'system' }` sur les factures nées d'un traitement —
   * génération depuis un CRA, cron d'abonnement — et `null` sur celles antérieures au suivi.
   *
   * **`null` n'est pas `system`** : « on ne sait pas » n'est pas « personne ». C'est le repli de
   * l'un sur l'autre qui avait produit le contresens corrigé par #877.
   *
   * Aucune facture antérieure n'en porte, et aucun rattrapage n'est possible : l'information
   * n'existe nulle part. La ligne se remplit à partir des prochaines émissions.
   */
  createdBy?: RecordAuthor | null;
};
